import React, { FC, useEffect, useState, useCallback, useMemo } from 'react';
import { Form, Input, Radio, Modal, message, Button, Popover } from 'antd';
import { Icon as LegacyIcon } from '@ant-design/compatible';
import { imageUploadValidation } from '../../utils/ImageUtils';
import { getBackendUri } from '../../utils/EnvConfigUtils';
import { retrieveCachedUserDetails } from '../../thunks/session_thunks';
import { compareStrings, nullSafeGet, nullSafeGetOrElse } from '../../utils/DataAccessUtils';
import { getProtectedImageUriForBuyer } from '../../utils/FileAccessUtils';
import OWUpload from '../OWUpload';
import { SITE_SURVEY_ANSWER_TYPE } from '../../utils/SiteSurveyUtils';
import { connect } from 'react-redux';
import WalkthroughTaskSection from '../ow_inline_todo_list/WalkthroughTaskSection';

const FORM_ID = 'walkthrough-answer-edit-form';
interface TaskEditFormProps {
	taskAnswer: any;
	onCancel: () => void;
	onSave: (answer: any) => any;
	walkthrough: any;
	currentUser: any;
}

interface FieldData {
	name: string;
	value?: any;
}

const TaskEditPopup: FC<TaskEditFormProps> = ({
	taskAnswer,
	onCancel,
	onSave,
	walkthrough,
}): React.ReactElement => {
	const [form] = Form.useForm();

	const [fields, setFields] = useState<FieldData[]>([]);
	const [attachments, setAttachments] = useState([]);
	const [uploading, setUploading] = useState(false);
	const [saving, setSaving] = useState(false);
	const [cancelling, setCancelling] = useState(false);
	const [previewVisible, setPreviewVisible] = useState(false);
	const [previewImage, setPreviewImage] = useState('');

	const BACKEND_URI = getBackendUri();
	const uploadHeaders = { 'X-Auth-Token': retrieveCachedUserDetails(['token']).token };

	const isTaskListAnswer = useMemo(
		() => taskAnswer.questionType === SITE_SURVEY_ANSWER_TYPE.TASK_LIST,
		[taskAnswer.questionType]
	);

	const initailList = useMemo(() => {
		const initialAttachments = (taskAnswer.attachments || []).map((p, idx) => {
			const imgFileName = nullSafeGetOrElse('fileName', p, '');
			const imgFileId = nullSafeGetOrElse('fileId', p, '');
			const thumbUri = getProtectedImageUriForBuyer(imgFileId, imgFileName, 100, 100);
			const fullUri = getProtectedImageUriForBuyer(imgFileId, imgFileName, 400, 400);
			return {
				uid: idx,
				size: 1,
				name: imgFileName,
				filename: imgFileName,
				url: fullUri,
				status: 'done',
				thumbUrl: thumbUri,
				response: { data: { fileName: imgFileName, fileId: imgFileId } },
				type: 'image/jpeg',
			};
		});
		setAttachments(initialAttachments);
		return initialAttachments;
	}, [taskAnswer]);

	useEffect(() => {
		setFields([
			{
				name: 'answer',
				value: taskAnswer.answer,
			},
			{
				name: 'comments',
				value: taskAnswer.comments,
			},
		]);
	}, [form, taskAnswer]);

	const getFormField = useCallback(
		(task) => {
			switch (task.questionType) {
				case SITE_SURVEY_ANSWER_TYPE.MULTIPLE_CHOICE:
					return nullSafeGetOrElse('multipleChoicesWithLabel.length', task, 0) !== 0 ? (
						<Radio.Group>
							{task.multipleChoicesWithLabel.map((choice, idx) => {
								const value = `${choice.value}${
									choice.description ? ` (${choice.description})` : ''
								}`;
								return (
									<Radio value={value} key={`rg-${idx}`}>
										{value}
									</Radio>
								);
							})}
						</Radio.Group>
					) : (
						''
					);
				case SITE_SURVEY_ANSWER_TYPE.TASK_LIST:
					return (
						<WalkthroughTaskSection
							notCheckable={false}
							existingTasks={nullSafeGetOrElse('taskListAnswer', task, [])}
							timeZoneId={nullSafeGet('location.timeZoneId', walkthrough)}
							associationDisabled
							addGroupDisabled
							isAllowedToEdit
							retainTaskObject
							walkthrough={walkthrough}
							siteSurveyTaskTitleId={nullSafeGetOrElse('titleId', task, '')}
							hideIndividualWalkthroughLink
						/>
					);
				default:
					return <Input.TextArea placeholder="" />;
			}
		},
		[walkthrough]
	);

	const handleImageUpdate = useCallback(
		(info) => {
			setUploading(info.file.status === 'uploading');

			if (info.file.status === 'done' || 'removed') {
				setAttachments(info.fileList);
			} else if (info.file.status === undefined) {
				info.fileList = attachments;
			}
		},
		[attachments]
	);

	const handleSubmit = useCallback(
		(values) => {
			setSaving(true);
			onSave({
				...taskAnswer,
				answer: isTaskListAnswer ? undefined : values.answer,
				comments: values.comments,
				attachments: attachments.map((attachment) => attachment.response.data),
			})
				.catch(message.error)
				.finally(() => setSaving(false));
		},
		[onSave, taskAnswer, isTaskListAnswer, attachments]
	);

	const handleCancel = useCallback(() => {
		setPreviewVisible(false);
		setPreviewImage('');
	}, []);

	const handleImagePreview = useCallback((obj) => {
		if (!obj.response.data) return;

		const imgFileId = obj.response.data.fileId;
		const imgFileName = obj.response.data.fileName;
		const thumbUrl = getProtectedImageUriForBuyer(imgFileId, imgFileName, 100, 100);
		const fullUrl = getProtectedImageUriForBuyer(imgFileId, imgFileName, 400, 400);
		setPreviewImage(fullUrl || thumbUrl);
		setPreviewVisible(true);
	}, []);
	const isImageUploading = (attachments || []).some((_) => _.status === 'uploading');

	const onModalCancel = useCallback(() => {
		if (cancelling) return;

		if (isTaskListAnswer) {
			setCancelling(true);
			onSave(taskAnswer)
				.catch(message.error)
				.finally(() => setCancelling(false));
		} else {
			onCancel();
		}
	}, [cancelling, isTaskListAnswer, onCancel, onSave, taskAnswer]);

	return (
		<Modal
			visible={true}
			title="Edit Answer"
			onCancel={onModalCancel}
			footer={[
				<Button onClick={onModalCancel} size="large" loading={cancelling}>
					Cancel
				</Button>,
				<Button
					type="primary"
					size="large"
					style={{ marginLeft: '16px' }}
					key="submit"
					htmlType="submit"
					form={FORM_ID}
					loading={saving || isImageUploading}
				>
					Save
				</Button>,
			]}
			closable
		>
			<Form id={FORM_ID} form={form} layout="vertical" fields={fields} onFinish={handleSubmit}>
				<Form.Item
					name="answer"
					label={taskAnswer.title}
					initialValue={
						taskAnswer.questionType === SITE_SURVEY_ANSWER_TYPE.TASK_LIST
							? taskAnswer.taskListAnswer
							: taskAnswer.answer
					}
				>
					{getFormField(taskAnswer)}
				</Form.Item>
				<Form.Item name="comments" label="Comments">
					<Input.TextArea />
				</Form.Item>
				<Form.Item name="attachments" label="Images">
					<OWUpload
						name="file"
						listType="picture-card"
						accept="image/*"
						multiple={true}
						defaultFileList={initailList}
						headers={uploadHeaders}
						onPreview={handleImagePreview}
						action={`${BACKEND_URI}/api/v1/buyer/file/upload`}
						beforeUpload={imageUploadValidation}
						onChange={handleImageUpdate}
					>
						<div style={{ display: 'inline' }}>
							<LegacyIcon type={uploading ? 'loading' : 'plus'} />
							<div className="ant-upload-text">Add Image</div>
						</div>
					</OWUpload>
					<Modal visible={previewVisible} footer={null} onCancel={handleCancel}>
						<img alt="example" style={{ width: '100%' }} src={previewImage} />
					</Modal>
				</Form.Item>
			</Form>
		</Modal>
	);
};

const mapStateToProps = (state) => ({
	currentUser: state.session.currentUser,
});

export default connect(mapStateToProps)(TaskEditPopup as any);
